import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ContactBar from "@/components/feature/ContactBar";
import SEOMeta from "@/components/base/SEOMeta";

const siteUrl = import.meta.env.VITE_SITE_URL || "";

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${siteUrl}/privacidade`,
  "name": "Política de Privacidade - RCRED Negócios Financeiros",
  "description": "Política de Privacidade da RCRED Negócios Financeiros. Saiba como tratamos seus dados pessoais, cookies e informações coletadas em nosso site.",
  "url": `${siteUrl}/privacidade`,
  "inLanguage": "pt-BR",
  "isPartOf": {
    "@id": `${siteUrl}/#website`
  }
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOMeta jsonLd={[pageSchema]} id="privacy-schema" />
      <Navbar />

      <main className="flex-1 pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <article className="max-w-4xl mx-auto">
            <header className="mb-10 md:mb-12">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-3">
                Política de Privacidade
              </h1>
              <p className="text-sm text-foreground-500">
                Última atualização: 11 de julho de 2026
              </p>
            </header>

            <div className="prose prose-foreground max-w-none space-y-6">
              <section>
                <p className="text-foreground-700 leading-relaxed">
                  A sua privacidade é importante para nós. É política do RCred Negócios respeitar a sua privacidade em relação a qualquer informação sua que possamos coletar no site{" "}
                  <a href="https://rcrednegocios.com.br" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">
                    RCred Negócios
                  </a>
                  , e outros sites que possuímos e operamos.
                </p>
              </section>

              <section>
                <p className="text-foreground-700 leading-relaxed">
                  Solicitamos informações pessoais apenas quando realmente precisamos delas para lhe fornecer um serviço. Fazemo-lo por meios justos e legais, com o seu conhecimento e consentimento. Também informamos por que estamos coletando e como será usado.
                </p>
              </section>

              <section>
                <p className="text-foreground-700 leading-relaxed">
                  Apenas retemos as informações coletadas pelo tempo necessário para fornecer o serviço solicitado. Quando armazenamos dados, protegemos dentro de meios comercialmente aceitáveis para evitar perdas e roubos, bem como acesso, divulgação, cópia, uso ou modificação não autorizados.
                </p>
              </section>

              <section>
                <p className="text-foreground-700 leading-relaxed">
                  Não compartilhamos informações de identificação pessoal publicamente ou com terceiros, exceto quando exigido por lei.
                </p>
              </section>

              <section>
                <p className="text-foreground-700 leading-relaxed">
                  O nosso site pode ter links para sites externos que não são operados por nós. Esteja ciente de que não temos controle sobre o conteúdo e práticas desses sites e não podemos aceitar responsabilidade por suas respectivas{" "}
                  <a href="https://politicaprivacidade.com/" rel="noopener noreferrer" target="_blank" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">
                    políticas de privacidade
                  </a>
                  .
                </p>
              </section>

              <section>
                <p className="text-foreground-700 leading-relaxed">
                  Você é livre para recusar a nossa solicitação de informações pessoais, entendendo que talvez não possamos fornecer alguns dos serviços desejados.
                </p>
              </section>

              <section>
                <p className="text-foreground-700 leading-relaxed">
                  O uso continuado de nosso site será considerado como aceitação de nossas práticas em torno de privacidade e informações pessoais. Se você tiver alguma dúvida sobre como lidamos com dados do usuário e informações pessoais, entre em contacto connosco.
                </p>
              </section>

              <section>
                <ul className="list-disc pl-5 space-y-3 text-foreground-700 leading-relaxed">
                  <li>
                    O serviço Google AdSense que usamos para veicular publicidade usa um cookie DoubleClick para veicular anúncios mais relevantes em toda a Web e limitar o número de vezes que um determinado anúncio é exibido para você.
                  </li>
                  <li>
                    Para mais informações sobre o Google AdSense, consulte as FAQs oficiais sobre privacidade do Google AdSense.
                  </li>
                  <li>
                    Utilizamos anúncios para compensar os custos de funcionamento deste site e fornecer financiamento para futuros desenvolvimentos. Os cookies de publicidade comportamental usados por este site foram projetados para garantir que você forneça os anúncios mais relevantes sempre que possível, rastreando anonimamente seus interesses e apresentando coisas semelhantes que possam ser do seu interesse.
                  </li>
                  <li>
                    Vários parceiros anunciam em nosso nome e os cookies de rastreamento de afiliados simplesmente nos permitem ver se nossos clientes acessaram o site através de um dos sites de nossos parceiros, para que possamos creditá-los adequadamente e, quando aplicável, permitir que nossos parceiros afiliados ofereçam qualquer promoção que pode fornecê-lo para fazer uma compra.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mt-10 mb-4">
                  Compromisso do Usuário
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-3">
                  O usuário se compromete a fazer uso adequado dos conteúdos e da informação que o RCred Negócios oferece no site e com caráter enunciativo, mas não limitativo:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-foreground-700 leading-relaxed">
                  <li>Não se envolver em atividades que sejam ilegais ou contrárias à boa fé e à ordem pública;</li>
                  <li>Não difundir propaganda ou conteúdo de natureza racista, xenofóbica, jogos de sorte ou azar, qualquer tipo de pornografia ilegal, de apologia ao terrorismo ou contra os direitos humanos;</li>
                  <li>Não causar danos aos sistemas físicos (hardwares) e lógicos (softwares) do RCred Negócios, de seus fornecedores ou terceiros, para introduzir ou disseminar vírus informáticos ou quaisquer outros sistemas de hardware ou software que sejam capazes de causar danos anteriormente mencionados.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-heading text-foreground-950 mt-10 mb-4">
                  Mais informações
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Esperemos que esteja esclarecido e, como mencionado anteriormente, se houver algo que você não tem certeza se precisa ou não, geralmente é mais seguro deixar os cookies ativados, caso interaja com um dos recursos que você usa em nosso site.
                </p>
                <p className="text-foreground-700 leading-relaxed mt-4">
                  Esta política é efetiva a partir de 11 de julho de 2026.
                </p>
              </section>
            </div>
          </article>
        </div>
      </main>

      <Footer />
      <ContactBar />
      <div className="h-12 md:h-0"></div>
    </div>
  );
}